import { FormGroup, FormArray, FormControl } from '@angular/forms';
import { hasIn, unset, get, set } from 'lodash';
import { merge } from 'rxjs';
import { distinctUntilChanged, tap, mergeMap } from 'rxjs/operators';

/**
 * Cherry picked from Angular Sources
 * to have a dasherize function at hands.
 */
const STRING_DASHERIZE_REGEXP = /[ _]/g;
const STRING_DECAMELIZE_REGEXP = /([a-z\d])([A-Z])/g;
const STRING_CAMELIZE_REGEXP = /(-|_|\.|\s)+(.)?/g;
function camelize(str) {
    return str
        .replace(STRING_CAMELIZE_REGEXP, (_match, _separator, chr) => {
        return chr ? chr.toUpperCase() : '';
    })
        .replace(/^([A-Z])/, (match) => match.toLowerCase());
}
function decamelize(str) {
    return str.replace(STRING_DECAMELIZE_REGEXP, '$1_$2').toLowerCase();
}
function dasherize(str) {
    return decamelize(str).replace(STRING_DASHERIZE_REGEXP, '-');
}

/**
 * Check if it's a form group and use a TS type guard to let TS know
 * that it is indeed a FormGroup in the control flow
 */
function checkIsFormGroup(control) {
    // duck test if this is a FormGroup
    return (control &&
        Object.prototype.hasOwnProperty.call(control, 'controls') &&
        control instanceof FormGroup);
}
function checkIsFormArray(control) {
    // duck test if this is a FormGroup
    return (control &&
        Object.prototype.hasOwnProperty.call(control, 'controls') &&
        control instanceof FormArray);
}
function checkIsFormControl(control) {
    return control && control instanceof FormControl;
}
function createControlItem({ control, controlPathSegments, isFormGroup, isFormArray }) {
    return {
        controlPath: controlPathSegments.join('.'),
        control,
        controlPathSegments,
        isFormGroup,
        isFormArray
    };
}
/**
 * walkFormGroup
 * @param {FormControl|FormGroup|FormArray} controlOrGroup
 * @param {object} options allows to set `includeFormGroups` parameter
 * @param {string} parentPath
 */
function walkFormGroup(controlOrGroup, { includeFormGroups = false } = {}, parentPath = []) {
    const items = [];
    const formGroupItem = createControlItem({
        controlPathSegments: parentPath,
        isFormGroup: checkIsFormGroup(controlOrGroup),
        isFormArray: checkIsFormArray(controlOrGroup),
        control: controlOrGroup
    });
    if (checkIsFormControl(controlOrGroup)) {
        items.push(formGroupItem);
    }
    else if (checkIsFormArray(controlOrGroup) || checkIsFormGroup(controlOrGroup)) {
        const entries = Object.entries(controlOrGroup.controls);
        const isRootFormGroup = parentPath.length === 0;
        if (true === includeFormGroups && false === isRootFormGroup) {
            items.push(formGroupItem);
        }
        for (const [path, nestedControl] of entries) {
            const currentPath = [...parentPath, path];
            items.push(...walkFormGroup(nestedControl, { includeFormGroups }, currentPath));
        }
    }
    return items;
}

const ACL_SEPARATOR = '/';
/**
 * Only map given explicit map of acl keys and control paths
 * @param aclMapping
 */
function mergeControlList(base, overrides = []) {
    function createDictionary(list) {
        return list.reduce((accu, item) => {
            accu.set(item.controlKey, item);
            return accu;
        }, new Map());
    }
    const baseMap = createDictionary(base);
    const overridesMap = createDictionary(overrides);
    const merged = new Map([...baseMap, ...overridesMap]);
    return [...merged.values()];
}
/**
 * Create a control list from all form controls in a form group including nested ones.
 */
function createControlListFromForm(form, verbose = false, excludes = []) {
    const flatControlList = walkFormGroup(form, { includeFormGroups: true });
    if (verbose) {
        console.groupCollapsed('ACL from FormGroup');
    }
    const items = flatControlList.reduce((accu, item) => {
        const aclKey = item.controlPathSegments
            .map((segment) => dasherize(segment))
            .join(ACL_SEPARATOR);
        if (excludes.includes(item.controlPath)) {
            if (verbose) {
                console.warn(`Ignoring '${item.controlPath}' as it's on the ignore list`);
            }
            return accu;
        }
        if (verbose) {
            console.log(`Mapping '${item.controlPath}' to aclKey '${aclKey}')`);
        }
        accu.push({
            aclKey: aclKey,
            controlKey: item.controlPath,
            control: item.control
        });
        return accu;
    }, []);
    if (verbose) {
        console.groupEnd();
    }
    return items;
}
/**
 * Create a manual mapping between form controls and acl keys.
 * The resulting array of AclControlItems can directly be consumed by `bindControlList`
 */
function createControlListFromMap(form, aclMapping = [], verbose = false) {
    if (verbose) {
        console.group('ACL from Map');
    }
    // use reduce to immediately remove invalid controls
    const items = aclMapping.reduce((accu, item) => {
        const control = form.get(item.controlPath);
        // We want to ignore control paths that are not found as we can't process anything without a control
        if (control === null) {
            if (verbose) {
                console.warn(`Ignoring '${item.controlPath}' as it's not existing (bound to acl key '${item.aclKey}')`);
            }
            return accu;
        }
        if (verbose) {
            console.log(`Adding '${item.controlPath}' bound to acl key '${item.aclKey}'`);
        }
        accu.push({
            aclKey: item.aclKey,
            controlKey: item.controlPath,
            control
        });
        return accu;
    }, []);
    if (verbose) {
        console.groupEnd();
    }
    return items;
}

/**
 * Given a form that was automatically bound (i.e. has acl keys that exactly match the form structure)
 * we can remove values from a given state (by using the control path) if the bound acl resource is read-only
 */
const clearAclReadOnlyValues = (aclControlList, acl) => (state) => {
    if (!state) {
        return;
    }
    // ensure we have a copy
    state = JSON.parse(JSON.stringify(state));
    function removeValueFromStateIf(path, condition) {
        if (hasIn(state, path) && condition()) {
            unset(state, path);
        }
    }
    // If ACL locks some fields no need to hydrate it ever — this must be coming from resources (if any)
    // DOUBT: setState & setResources will never appear in fixed order. Most of the time
    // Resources will come after state and that multiple times
    for (const item of aclControlList) {
        const aclEditDenied = false === acl.canEdit(item.aclKey);
        if (aclEditDenied) {
            // console.log('delete that', item.aclKey, item.controlKey, state, state[item.controlKey]);
        }
        removeValueFromStateIf(item.controlKey, () => aclEditDenied);
    }
    return state;
};

/**
 * Disable or Enable a control for a given aclAttribute
 */
function syncControlEdit(control, aclObservable) {
    return aclObservable.pipe(distinctUntilChanged(), tap((editable) => {
        if (!editable) {
            control.disable();
        }
        else {
            control.enable();
        }
    }));
}

/**
 * Remove or Add a form control by it's control key.
 * The control must be available on the given form, will be cached through the closure
 */
function syncControlView(form, controlKey, aclObservable) {
    const cacheExistingControl = form.get(controlKey);
    return aclObservable.pipe(distinctUntilChanged(), tap((show) => {
        if (!cacheExistingControl) {
            console.warn(`Could not enable/disable form control '${controlKey}' through ACL, because the form does not contain a control with this key. Please check your ACL policy.`);
            return;
        }
        const { group } = splitPathIntoGroupAndControlKey(form, controlKey);
        if (group instanceof FormArray) {
            console.warn(`Could not enable ACL for '${controlKey}', parent AbstractControl is a FormArray.
          We do not support FormArray > FormControl relationships. Consider excluding it from the ACL mapping or use a FormArray > FormGroup > FormControl hierarchy.`);
            return;
        }
        if (!show) {
            removeNestedControl(form, controlKey);
        }
        else {
            addNestedControl(form, controlKey, cacheExistingControl);
        }
    }));
}
/**
 * Support
 */
function removeNestedControl(form, path) {
    const { group, controlName } = splitPathIntoGroupAndControlKey(form, path);
    if (group instanceof FormGroup) {
        group.removeControl(controlName);
    }
    else {
        console.warn(`could not process '${path}', parent form group of key '${controlName}' not valid`);
    }
}
function addNestedControl(form, path, control) {
    const { group, controlName } = splitPathIntoGroupAndControlKey(form, path);
    if (group instanceof FormGroup) {
        group.addControl(controlName, control);
    }
    else {
        console.warn(`could not process '${path}', parent form group of key '${controlName}' not valid`);
    }
}
/**
 * Helper to get proper access to a control and it's parent group.
 * We can't use form.addControl('some.path', control) as this would use 'some.path' as the key
 * and not access the group `some` and the the control `path` there.
 *
 * This helper does a proper traversal and returns the group and the matching control key (last part of the path)
 * which can the be used to access group.addControl and group.removeControl
 */
function splitPathIntoGroupAndControlKey(form, path) {
    var _a;
    const pathSegments = path.split('.');
    if (pathSegments.length === 1) {
        return {
            group: form,
            controlName: pathSegments[0]
        };
    }
    const controlName = (_a = pathSegments.pop()) !== null && _a !== void 0 ? _a : path;
    const parentPath = pathSegments.join('.');
    // we just assume this is a form group
    const group = form.get(parentPath);
    return {
        group: group,
        controlName: controlName
    };
}

/**
 * Given a list of AclControlItem use a factory to create streams
 * for each control which will adjust depending on a given acl key subscription.
 * The entire list is merged to listen for future changes afterwards.
 */
function controlEnforcer(controls, controlEnforcerFactory) {
    const items = controls.map((value) => controlEnforcerFactory(value));
    return merge(items).pipe(mergeMap((value) => value));
}

/**
 * Consume a list of AclControlItem where each entry corresponds to a control with an acl key.
 */
const bindControlList = (aclControlList, acl, form) => {
    return merge(controlEnforcer(aclControlList, ({ control, aclKey }) => syncControlEdit(control, acl.canEdit$(aclKey))), controlEnforcer(aclControlList, (item) => syncControlView(form, item.controlKey, acl.canShow$(item.aclKey))));
};

/**
 * Given a form that was automatically bound (i.e. has acl keys that exactly match the form structure)
 * we can use the given convention between the control path to get a patch object for all read all values
 * from a given state that matches the form group hierarchy.
 */
const getAclPatchObject = (aclControlList, form, acl) => (state) => {
    const result = {};
    function storeValueForControl(aclKey) {
        const formKey = camelize(aclKey).split('/').join('.');
        const value = get(state, formKey);
        const controlExists = !!form.get(formKey);
        if (controlExists && value) {
            set(result, formKey, value);
        }
    }
    for (const item of aclControlList) {
        const aclEditDenied = false === acl.canEdit(item.aclKey);
        const aclShowGranted = acl.canShow(item.aclKey);
        if (aclEditDenied && aclShowGranted) {
            storeValueForControl(item.aclKey);
        }
    }
    return result;
};

const autoFormBindingFactory = ({ overrides = [], excludes = [] } = {}) => (acl, form) => {
    if (!form) {
        throw new Error('Trying to ACL-bind a malformed form. The given form is probably null or undefined.');
    }
    const verbose = false;
    const aclControlList = createControlListFromForm(form, verbose, excludes);
    const aclControlListOverrides = createControlListFromMap(form, overrides);
    const mergedList = mergeControlList(aclControlList, aclControlListOverrides);
    const stream$ = bindControlList(mergedList, acl, form);
    const clearFn = clearAclReadOnlyValues(aclControlList, acl);
    const patchFn = getAclPatchObject(aclControlList, form, acl);
    return {
        stream$,
        clearAclReadOnlyValues: clearFn,
        getPatchObject: patchFn
    };
};

/**
 * Use `createControlListFromMap` to bind a manually curated map of controls & acl keys
 */
const manualFormBindingFactory = (aclMapping = []) => (acl, form) => {
    const verbose = false;
    const aclControlList = createControlListFromMap(form, aclMapping, verbose);
    return bindControlList(aclControlList, acl, form);
};

/**
 * ACL Form Binding Factories
 */

/**
 * Generated bundle index. Do not edit.
 */

export { autoFormBindingFactory, clearAclReadOnlyValues, getAclPatchObject, manualFormBindingFactory };
